// // services/internship.service.ts

import { axiosInstance } from "@/lib/axiosInstance";




/**
 * The student app filters internships by these five fixed chips. The API has a
 * Literal enum on create/update and rejects anything else with a 422; `null`
 * means unclassified, which shows under the student app "All" tab only.
 */
export const INTERNSHIP_DOMAINS = [
  "Tech",
  "Design",
  "Marketing",
  "Finance",
  "Operations",
] as const;

export type InternshipDomain = (typeof INTERNSHIP_DOMAINS)[number];

export interface InternshipType {
  id: string;
  title: string;
  description: string;
  type: string;
  // Reads stay permissive: rows created before the enum can still hold free text.
  domain: string | null;
  location: string;
  is_remote: boolean;
  stipend_min: number;
  stipend_max: number;
  openings: number;
  duration_weeks: number;
  start_date: string;
  application_deadline: string;
  required_skills: string[];
  requires_partnership: boolean;
  target_degree_types: string[];
  createdAt: string;
  updatedAt: string;
  status?: string;
}

export interface InternshipCreateType {
  title: string;
  description: string;
  type: string;
  domain: InternshipDomain | null;
  location: string;
  is_remote: boolean;
  stipend_min: number;
  stipend_max: number;
  openings: number;
  duration_weeks: number;
  start_date: string;
  application_deadline: string;
  required_skills: string[];
  requires_partnership: boolean;
  target_degree_types: string[];
}

export interface InternshipMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type InternshipParamsType = {
  search?: string;
  status?: string;
  type?: string;
  page?: number;
  limit?: number;
};

export interface InternshipApplication {
  id: string;
  student_application_id: string;
  internship_id: string;
  student_id: string;
  college_id: string;
  resume_url: string;
  cover_letter: string | null;
  status: string;
  applied_at: string;
  updated_at: string;
}

// Interview types and calls live in `interview.service.ts` — keep them there so
// there is a single source of truth for the /interviews endpoints.

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllInternships = async (filters: InternshipParamsType) => {
  const res = await axiosInstance.get("/v1/internships", {
    params: filters,
  });

  const data = res.data;
  const items = data.items || [];
  const meta = data.meta || {};
  
  // Safely extract pagination info, defaulting to 1 page if missing
  const total = data.total || meta.pagination?.total || meta.total || 0;
  const page = data.page || meta.pagination?.page || meta.page || filters.page || 1;
  const limit = data.limit || meta.pagination?.limit || meta.limit || filters.limit || 10;
  
  return {
    internships: items,
    metaData: {
      pagination: {
        total,
        page,
        limit,
        totalPages: data.totalPages || meta.pagination?.totalPages || meta.totalPages || Math.ceil(total / limit) || 1,
      }
    },
  };
};

export const fetchInternshipById = async (id: string) => {
  const res = await axiosInstance.get(`/v1/internships/${id}`);
  return res.data;
};

export const fetchInternshipApplications = async (id: string, params?: { page?: number; limit?: number; status?: string }) => {
  const res = await axiosInstance.get(`/v1/internships/${id}/applications`, { params });
  return res.data;
};

export const updateApplicationStatus = async (applicationId: string, status: string) => {
  const res = await axiosInstance.patch(`/v1/applications/${applicationId}/status`, { status });
  return res.data;
};

export const createInternship = async (data: InternshipCreateType) => {
  const res = await axiosInstance.post("/v1/internships", data);
  return res.data;
};

export const updateInternship = async (
  id: string,
  data: InternshipCreateType
) => {
  const res = await axiosInstance.put(`/v1/internships/${id}`, data);
  return res.data;
};

export const publishInternship = async (id: string) => {
  return axiosInstance.patch(`/v1/internships/${id}/publish`);
};

export const closeInternship = async (id: string) => {
  return axiosInstance.patch(`/v1/internships/${id}/close`);
};

export const deleteInternship = async (id: string) => {
  const res = await axiosInstance.delete(`/v1/internships/${id}`);
  return res.data;
};


















// export type InternshipStatus = "active" | "pending" | "completed";
// export type InternshipMode = "On-site" | "Remote" | "Hybrid";

// export interface InternshipType {
//   _id: string;
//   position: string; // e.g. "Software Development Intern"
//   company: string; // Vendor ID
//   students: string[]; // Array of Student IDs
//   departments: string[]; // Array of Dept IDs/Names
//   batches: string[]; // Array of Batch IDs
//   duration: string; // e.g. "3 months"
//   stipend: number; // monthly stipend in USD, e.g. 2500
//   mode: InternshipMode;
//   startDate: string; // ISO date string
//   endDate?: string; // ISO date string
//   status: InternshipStatus;
//   description?: string;
//   requirements?: string;
//   createdAt: string;
//   updatedAt: string;
// }



// export interface InternshipCreateType {
//   position: string;
//   company: string;
//   students: string[];
//   departments: string[];
//   batches: string[];
//   duration: string;
//   stipend: number;
//   mode: InternshipMode;
//   startDate: string;
//   endDate?: string;
//   status: InternshipStatus;
//   description?: string;
//   requirements?: string;
// }



// export interface InternshipMetaType {
//   pagination: {
//     total: number;
//     page: number;
//     limit: number;
//     totalPages: number;
//   };
// }

// export type InternshipParamsType = {
//   search?: string;
//   status?: InternshipStatus | "all";
//   page?: number;
//   limit?: number;
// };

// // ─── Dummy Data ───────────────────────────────────────────────────────────────

// const DUMMY_INTERNSHIPS: InternshipType[] = [
//   {
//     _id: "1",
//     position: "Software Development Intern",
//     company: "1", // TechCorp Solutions
//     students: ["1", "4"], // Alex Johnson, Sophia Brown
//     departments: ["CSE"],
//     batches: ["1"], // CSE 2021-2025
//     duration: "3 months",
//     stipend: 2500,
//     mode: "On-site",
//     startDate: "2025-06-01",
//     endDate: "2025-09-01",
//     status: "active",
//     description: "Hands-on software development experience.",
//     requirements: "Proficient in React and Node.js",
//     createdAt: "2025-05-01T00:00:00.000Z",
//     updatedAt: "2025-05-01T00:00:00.000Z",
//   },
//   {
//     _id: "2",
//     position: "Data Science Intern",
//     company: "2", // InnovateLabs
//     students: ["2"], // Emma Watson
//     departments: ["IT"],
//     batches: ["3"], // IT 2021-2025
//     duration: "6 months",
//     stipend: 2800,
//     mode: "Hybrid",
//     startDate: "2025-05-15",
//     endDate: "2025-11-15",
//     status: "active",
//     description: "Working on AI models for healthcare.",
//     requirements: "Python and ML basics",
//     createdAt: "2025-04-15T00:00:00.000Z",
//     updatedAt: "2025-04-15T00:00:00.000Z",
//   },
// ];

// // ─── Helpers ──────────────────────────────────────────────────────────────────

// const simulateDelay = (ms = 400) =>
//   new Promise((resolve) => setTimeout(resolve, ms));

// const applyFilters = (
//   records: InternshipType[],
//   filters: InternshipParamsType
// ): { data: InternshipType[]; total: number } => {
//   let result = [...records];

//   if (filters.search) {
//     const q = filters.search.toLowerCase();
//     result = result.filter(
//       (r) =>
//         r.position.toLowerCase().includes(q) ||
//         // r.company.toLowerCase().includes(q) || // Needs lookup if searching by company name
//         r.description?.toLowerCase().includes(q)
//     );
//   }

//   if (filters.status && filters.status !== "all") {
//     result = result.filter((r) => r.status === filters.status);
//   }

//   const total = result.length;
//   const page = filters.page ?? 1;
//   const limit = filters.limit ?? 10;
//   result = result.slice((page - 1) * limit, page * limit);

//   return { data: result, total };
// };

// // ─── Service Functions ────────────────────────────────────────────────────────

// export const fetchAllInternships = async (filters: InternshipParamsType) => {
//   await simulateDelay();
//   // TODO: const res = await axiosInstance.get("/internships/", { params: filters });
//   const limit = filters.limit ?? 10;
//   const page = filters.page ?? 1;
//   const { data, total } = applyFilters(DUMMY_INTERNSHIPS, filters);
//   return {
//     internships: data,
//     metaData: {
//       pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
//     } as InternshipMetaType,
//   };
// };

// export const fetchInternshipById = async (_id: string) => {
//   await simulateDelay();
//   // TODO: const res = await axiosInstance.get(`/internships/${_id}`);
//   const record = DUMMY_INTERNSHIPS.find((r) => r._id === _id);
//   if (!record) throw new Error("Internship not found");
//   return record;
// };

// export const createInternship = async (data: InternshipCreateType) => {
//   await simulateDelay();
//   // TODO: const res = await axiosInstance.post("/internships/", data);
//   const newRecord: InternshipType = {
//     ...data,
//     _id: String(Date.now()),
//     createdAt: new Date().toISOString(),
//     updatedAt: new Date().toISOString(),
//   };
//   DUMMY_INTERNSHIPS.push(newRecord);
//   return newRecord;
// };

// export const updateInternship = async (
//   _id: string,
//   data: InternshipCreateType
// ) => {
//   await simulateDelay();
//   // TODO: const res = await axiosInstance.put(`/internships/${_id}`, data);
//   const index = DUMMY_INTERNSHIPS.findIndex((r) => r._id === _id);
//   if (index === -1) throw new Error("Internship not found");
//   DUMMY_INTERNSHIPS[index] = {
//     ...DUMMY_INTERNSHIPS[index],
//     ...data,
//     updatedAt: new Date().toISOString(),
//   };
//   return DUMMY_INTERNSHIPS[index];
// };

// export const deleteInternship = async (_id: string) => {
//   await simulateDelay();
//   // TODO: const res = await axiosInstance.delete(`/internships/${_id}`);
//   const index = DUMMY_INTERNSHIPS.findIndex((r) => r._id === _id);
//   if (index === -1) throw new Error("Internship not found");
//   const [deleted] = DUMMY_INTERNSHIPS.splice(index, 1);
//   return deleted;
// };
